import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { trackQuotePDFExport } from '../lib/analytics';
import { 
  Download, 
  CheckCircle, 
  Info, 
  AlertCircle, 
  Eye, 
  Layers,
  RefreshCw,
  TreeDeciduous,
  GitCompare,
  Settings2,
  FileDown,
  Settings,
  Lock,
  X,
} from 'lucide-react';

// Default filament prices (USD per kg)
const DEFAULT_PRICES = {
  PLA: 20,
  ABS: 22,
  PETG: 25,
  TPU: 35,
  PVA: 60,
  HIPS: 25,
};

/**
 * Support Generation Results Component
 */
export const SupportResults = ({
  supportResult,
  mode,
  onDownload,
  onPreview,
  onReset,
  onRegenerate,
  currentSettings,
  customPrices,
  user,
}) => {
  const navigate = useNavigate();
  const [showRegenerateOptions, setShowRegenerateOptions] = useState(false);

  // Quote state
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showUpgradeHint, setShowUpgradeHint] = useState(false);
  const [quoteName, setQuoteName] = useState('');
  const [quoteMarkup, setQuoteMarkup] = useState(50);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  const isFreePlan = !user?.subscription_plan || user.subscription_plan === 'free';

  const handleExportQuote = () => {
    if (isFreePlan) {
      setShowUpgradeHint(true);
      return;
    }
    setQuoteName(supportResult?.original_filename?.replace('.stl', '') || 'Project');
    setQuoteError('');
    setShowQuoteModal(true);
  };

  const downloadQuotePDF = async () => {
    if (!supportResult) return;
    trackQuotePDFExport();
    setQuoteLoading(true);
    setQuoteError('');
    try {
      const response = await api.post(
        `/supports/${supportResult.id}/quote-pdf`,
        {
          project_name: quoteName || 'Project',
          markup_percent: quoteMarkup,
          notes: quoteNotes,
        },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quoteName || 'quote'}_quote.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowQuoteModal(false);
    } catch (err) {
      setQuoteError(err.response?.data?.detail || 'Failed to generate quote PDF');
    } finally {
      setQuoteLoading(false);
    }
  };
  // Calculate costs based on custom prices
  const calculatedCosts = useMemo(() => {
    if (!supportResult?.cost_estimate?.filament_usage_grams) return {};
    
    const costs = {};
    const filamentUsage = supportResult.cost_estimate.filament_usage_grams;
    
    for (const [material, grams] of Object.entries(filamentUsage)) {
      const pricePerKg = customPrices[material] || DEFAULT_PRICES[material] || 20;
      const pricePerGram = pricePerKg / 1000;
      costs[material] = (grams * pricePerGram).toFixed(2);
    }
    
    return costs;
  }, [supportResult, customPrices]);

  if (!supportResult || !supportResult.ai_explanation) return null;

  return (
    <div className="space-y-6" data-testid="support-results">
      {/* Preview Button */}
      <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 p-6 rounded-lg border border-purple-500/30">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <Eye className="w-6 h-6 text-purple-400" />
              3D Preview
            </h2>
            <p className="text-text-secondary text-sm">
              Inspect your model with supports in 3D before downloading
            </p>
          </div>
          <button
            onClick={onPreview}
            className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
            data-testid="preview-supports-btn"
          >
            <Eye className="w-5 h-5" />
            Open 3D Preview
          </button>
        </div>
      </div>

      {/* Download Options */}
      <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
        <h2 className="text-xl font-bold text-white mb-4">Download Your File</h2>
        <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-blue-400 text-sm font-semibold mb-2">📦 Export Options</p>
          <p className="text-text-secondary text-sm">
            Choose your preferred format. STL is universally supported, while 3MF includes advanced metadata.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => onDownload('stl')}
            className="px-6 py-4 bg-accent hover:bg-accent-hover text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            data-testid="download-stl-btn"
          >
            <Download className="w-5 h-5" />
            Download STL
          </button>
          
          <button
            onClick={() => onDownload('3mf')}
            className="px-6 py-4 border-2 border-accent text-accent hover:bg-accent hover:text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            data-testid="download-3mf-btn"
          >
            <Download className="w-5 h-5" />
            Download 3MF
          </button>
          
          {mode === 'pro' && (
            <button
              onClick={() => onDownload('gcode')}
              className="px-6 py-4 border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="download-gcode-btn"
            >
              <Download className="w-5 h-5" />
              Download G-code
            </button>
          )}
        </div>

        {/* Export Quote PDF */}
        <button
          onClick={handleExportQuote}
          className={`mt-4 w-full px-6 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
            isFreePlan
              ? 'bg-emerald-600/30 text-white/60 cursor-not-allowed border border-emerald-500/20'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white'
          }`}
          data-testid="export-quote-btn"
        >
          {isFreePlan ? <Lock className="w-5 h-5" /> : <FileDown className="w-5 h-5" />}
          Export Quote PDF
          {isFreePlan && <span className="text-[10px] bg-white/15 px-1.5 py-0.5 rounded ml-1">PRO</span>}
        </button>

        <div className="mt-4 p-3 bg-bg-dark rounded border border-text-muted/20">
          <p className="text-text-secondary text-xs">
            💡 If automatic download fails, right-click the button and select Save Link As
          </p>
        </div>
      </div>

      {/* Regenerate with Different Settings */}
      <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <RefreshCw className="w-6 h-6 text-yellow-400" />
            Try Different Settings
          </h2>
          <button
            onClick={() => setShowRegenerateOptions(!showRegenerateOptions)}
            className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg transition flex items-center gap-2"
            data-testid="toggle-regenerate-options"
          >
            <Settings2 className="w-4 h-4" />
            {showRegenerateOptions ? 'Hide Options' : 'Show Options'}
          </button>
        </div>
        
        <p className="text-text-secondary text-sm mb-4">
          Not happy with the results? Try regenerating with different settings to compare.
        </p>

        {showRegenerateOptions && (
          <div className="space-y-4 p-4 bg-bg-dark/50 rounded-lg border border-yellow-500/20">
            {/* Quick Regenerate Options */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Switch Tree Style */}
              <button
                onClick={() => onRegenerate && onRegenerate({
                  treeStyle: currentSettings?.treeStyle === 'hierarchical' ? 'forest' : 'hierarchical'
                })}
                className="p-4 bg-bg-dark rounded-lg border border-text-muted/20 hover:border-accent/50 transition text-left"
                data-testid="regenerate-switch-style"
              >
                <div className="flex items-center gap-3 mb-2">
                  <TreeDeciduous className="w-5 h-5 text-green-400" />
                  <span className="text-white font-medium">Switch to {currentSettings?.treeStyle === 'hierarchical' ? 'Forest' : 'Hierarchical'} Mode</span>
                </div>
                <p className="text-text-secondary text-xs">
                  {currentSettings?.treeStyle === 'hierarchical' 
                    ? 'Try multiple independent trees for better coverage'
                    : 'Try organic OrcaSlicer-style supports'}
                </p>
              </button>

              {/* Compare Both Modes */}
              <button
                onClick={() => onRegenerate && onRegenerate({ compareMode: true })}
                className="p-4 bg-bg-dark rounded-lg border border-text-muted/20 hover:border-purple-500/50 transition text-left"
                data-testid="regenerate-compare"
              >
                <div className="flex items-center gap-3 mb-2">
                  <GitCompare className="w-5 h-5 text-purple-400" />
                  <span className="text-white font-medium">Compare Both Modes</span>
                </div>
                <p className="text-text-secondary text-xs">
                  Generate Forest AND Hierarchical to compare results
                </p>
              </button>

              {/* Change Density */}
              <button
                onClick={() => onRegenerate && onRegenerate({
                  density: currentSettings?.density === 'standard' ? 'dense' : 'standard'
                })}
                className="p-4 bg-bg-dark rounded-lg border border-text-muted/20 hover:border-blue-500/50 transition text-left"
                data-testid="regenerate-density"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Layers className="w-5 h-5 text-blue-400" />
                  <span className="text-white font-medium">
                    {currentSettings?.density === 'standard' ? 'Increase' : 'Decrease'} Density
                  </span>
                </div>
                <p className="text-text-secondary text-xs">
                  {currentSettings?.density === 'standard' 
                    ? 'More supports for maximum strength'
                    : 'Standard density for balanced results'}
                </p>
              </button>

              {/* Start Fresh */}
              <button
                onClick={onReset}
                className="p-4 bg-bg-dark rounded-lg border border-text-muted/20 hover:border-red-500/50 transition text-left"
                data-testid="start-fresh-btn"
              >
                <div className="flex items-center gap-3 mb-2">
                  <RefreshCw className="w-5 h-5 text-red-400" />
                  <span className="text-white font-medium">Start Fresh</span>
                </div>
                <p className="text-text-secondary text-xs">
                  Upload a new model or change all settings
                </p>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Orientation Optimization Info */}
      {supportResult.orientation_optimization?.rotation_applied && (
        <div className="bg-blue-500/10 border border-blue-500/30 p-6 rounded-lg">
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-blue-400" />
            Orientation Optimized
          </h2>
          <p className="text-text-secondary mb-3">
            {supportResult.orientation_optimization.rotation_description}
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-bg-dark p-4 rounded-lg">
              <p className="text-text-secondary text-sm mb-1">Support Reduction</p>
              <p className="text-2xl font-bold text-blue-400">
                {supportResult.orientation_optimization.support_reduction_estimate}
              </p>
            </div>
            <div className="bg-bg-dark p-4 rounded-lg">
              <p className="text-text-secondary text-sm mb-1">Stability Score</p>
              <p className="text-2xl font-bold text-blue-400">
                {typeof supportResult.orientation_optimization.stability_score === 'number' 
                  ? supportResult.orientation_optimization.stability_score.toFixed(1) 
                  : supportResult.orientation_optimization.stability_score}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Summary */}
      <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
        <h2 className="text-xl font-bold text-white mb-4">AI Analysis</h2>
        <p className="text-text-secondary mb-4">{supportResult.ai_explanation.summary}</p>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <h3 className="text-white font-semibold mb-2">Quality Score</h3>
            <div className="text-3xl font-bold text-accent">
              {supportResult.ai_explanation.quality_score || 'N/A'}/100
            </div>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-2">Processing Time</h3>
            <div className="text-3xl font-bold text-accent">
              {supportResult.processing_time?.toFixed(1)}s
            </div>
          </div>
        </div>
      </div>

      {/* Support Statistics */}
      <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
        <h2 className="text-xl font-bold text-white mb-4">Support Statistics</h2>
        
        {mode === 'pro' && supportResult.support_count > 0 && (
          <div className="mb-4 p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
            <p className="text-green-400 font-semibold flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {supportResult.support_count} support structures generated!
            </p>
            <p className="text-green-300 text-sm mt-2">
              ✓ Supports are embedded in the G-code file<br/>
              ✓ Download the G-code (green button) to see them<br/>
              ✓ Open in any G-code viewer or slicer
            </p>
          </div>
        )}
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-bg-dark p-4 rounded-lg">
            <p className="text-text-secondary text-sm mb-1">Support Sections</p>
            <p className="text-white text-2xl font-bold">{supportResult.support_count || 0}</p>
            <p className="text-xs text-text-secondary mt-1">In G-code</p>
          </div>
          <div className="bg-bg-dark p-4 rounded-lg">
            <p className="text-text-secondary text-sm mb-1">Material Efficiency</p>
            <p className="text-white text-2xl font-bold">
              {supportResult.ai_explanation.material_efficiency || 'N/A'}
            </p>
          </div>
          <div className="bg-bg-dark p-4 rounded-lg">
            <p className="text-text-secondary text-sm mb-1">Print Success</p>
            <p className="text-white text-2xl font-bold">
              {supportResult.ai_explanation.print_success_improvement || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Easy-Peel Heatmap Summary */}
      {supportResult.contact_points && supportResult.contact_points.length > 0 && (
        <div data-testid="heatmap-summary" className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
            </svg>
            Easy-Peel Heatmap
          </h2>
          <p className="text-text-secondary text-sm mb-4">
            Contact point analysis shows where supports touch your model and how difficult they are to remove.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-bg-dark p-3 rounded-lg text-center">
              <div className="w-4 h-4 rounded-full bg-green-500 mx-auto mb-2" />
              <p className="text-green-400 text-2xl font-bold">
                {supportResult.contact_points.filter(p => p.label === 'easy').length}
              </p>
              <p className="text-xs text-text-secondary">Easy peel</p>
            </div>
            <div className="bg-bg-dark p-3 rounded-lg text-center">
              <div className="w-4 h-4 rounded-full bg-orange-500 mx-auto mb-2" />
              <p className="text-orange-400 text-2xl font-bold">
                {supportResult.contact_points.filter(p => p.label === 'moderate').length}
              </p>
              <p className="text-xs text-text-secondary">Moderate</p>
            </div>
            <div className="bg-bg-dark p-3 rounded-lg text-center">
              <div className="w-4 h-4 rounded-full bg-red-500 mx-auto mb-2" />
              <p className="text-red-400 text-2xl font-bold">
                {supportResult.contact_points.filter(p => p.label === 'hard').length}
              </p>
              <p className="text-xs text-text-secondary">Hard</p>
            </div>
          </div>
          <button
            onClick={onPreview}
            className="w-full mt-3 py-2.5 bg-purple-500/15 text-purple-400 text-sm font-medium rounded-lg hover:bg-purple-500/25 transition flex items-center justify-center gap-2"
            data-testid="open-heatmap-3d-btn"
          >
            <Eye className="w-4 h-4" />
            View Heatmap in 3D
          </button>
        </div>
      )}

      {/* Cost Estimator */}
      {supportResult.cost_estimate && supportResult.cost_estimate.support_volume_mm3 > 0 && (
        <CostEstimatorSection
          costEstimate={supportResult.cost_estimate}
          customPrices={customPrices}
          calculatedCosts={calculatedCosts}
          onGoToSettings={() => navigate('/dashboard/settings')}
        />
      )}

      {/* Multi-Material / Dual Extruder Support */}
      {supportResult.support_only_file_id && (
        <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 p-6 rounded-lg border border-orange-500/30">
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <Layers className="w-6 h-6 text-orange-400" />
            Dual Extruder / Multi-Material
          </h2>
          <p className="text-text-secondary mb-4">
            Download supports as a separate STL file for use with dual extruder printers. 
            Print supports with soluble filament (PVA, HIPS) for easy removal.
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <button
              onClick={() => onDownload('support-only')}
              className="px-6 py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="download-supports-only-btn"
            >
              <Download className="w-5 h-5" />
              Download Supports Only (STL)
            </button>
            <button
              onClick={() => onDownload('model-only')}
              className="px-6 py-4 border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="download-model-only-btn"
            >
              <Download className="w-5 h-5" />
              Download Model Only (STL)
            </button>
          </div>
          
          <div className="bg-bg-dark/50 p-4 rounded-lg">
            <p className="text-sm text-text-secondary mb-2">
              <span className="text-orange-400 font-semibold">💡 Tip:</span> For best results with soluble supports:
            </p>
            <ul className="text-sm text-text-secondary space-y-1 ml-4">
              <li>• <span className="text-white">PVA</span> - Dissolves in water, works with PLA</li>
              <li>• <span className="text-white">HIPS</span> - Dissolves in limonene, works with ABS</li>
              <li>• Load model on Extruder 1, supports on Extruder 2 in your slicer</li>
            </ul>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {supportResult.ai_explanation.recommendations && (
        <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
          <h2 className="text-xl font-bold text-white mb-4">Printing Recommendations</h2>
          <ul className="space-y-2">
            {supportResult.ai_explanation.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-text-secondary">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Critical Regions */}
      {supportResult.critical_regions && supportResult.critical_regions.length > 0 && (
        <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
          <h2 className="text-xl font-bold text-white mb-4">Critical Support Regions</h2>
          <ul className="space-y-2">
            {supportResult.critical_regions.map((region, idx) => (
              <li key={idx} className="flex items-start gap-2 text-text-secondary">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span>{region}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* New Generation Button */}
      <button
        onClick={onReset}
        className="w-full px-6 py-3 border border-text-muted/30 hover:border-accent/50 text-white rounded-lg font-semibold transition-colors"
        data-testid="generate-another-btn"
      >
        Generate Supports for Another File
      </button>

      {/* Upgrade Hint Modal */}
      {showUpgradeHint && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="upgrade-modal">
          <div className="bg-bg-light rounded-xl border border-primary/30 w-full max-w-sm p-6 space-y-4 text-center">
            <div className="w-12 h-12 bg-primary/15 rounded-full flex items-center justify-center mx-auto">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-text-primary">Pro Feature</h3>
            <p className="text-sm text-text-secondary leading-relaxed">
              Professional PDF quote export is available on the <span className="text-primary font-semibold">Pro</span> and <span className="text-accent font-semibold">Studio</span> plans.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setShowUpgradeHint(false)}
                className="flex-1 px-4 py-2.5 bg-bg-dark border border-text-muted/20 text-text-secondary rounded-lg text-sm transition hover:text-text-primary"
                data-testid="upgrade-dismiss-btn"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowUpgradeHint(false);
                  window.location.href = '/#pricing';
                }}
                className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition"
                data-testid="upgrade-cta-btn"
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote PDF Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-light rounded-xl border border-text-muted/20 w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <FileDown className="w-5 h-5 text-emerald-400" />
                Export Quote PDF
              </h3>
              <button onClick={() => setShowQuoteModal(false)} data-testid="close-quote-modal">
                <X className="w-5 h-5 text-text-muted hover:text-text-primary" />
              </button>
            </div>

            {quoteError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                {quoteError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Project Name</label>
                <input
                  type="text"
                  value={quoteName}
                  onChange={(e) => setQuoteName(e.target.value)}
                  placeholder="Enter project name"
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="quote-project-name"
                />
              </div>
              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Markup Percentage (%)</label>
                <input
                  type="number"
                  value={quoteMarkup}
                  onChange={(e) => setQuoteMarkup(parseInt(e.target.value) || 0)}
                  min="0"
                  max="500"
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="quote-markup"
                />
                <p className="text-xs text-text-muted mt-1">Applied on top of material + electricity costs</p>
              </div>
              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Notes (optional)</label>
                <textarea
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Add custom notes to the quote..."
                  rows={3}
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 resize-none"
                  data-testid="quote-notes"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={downloadQuotePDF}
                disabled={quoteLoading}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition disabled:opacity-50"
                data-testid="generate-quote-pdf-btn"
              >
                <FileDown className="w-4 h-4" />
                {quoteLoading ? 'Generating...' : 'Download PDF'}
              </button>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2.5 text-text-secondary hover:text-text-primary transition text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Cost Estimator Section — read-only, pulls prices from user Settings
 */
const CostEstimatorSection = ({
  costEstimate,
  customPrices,
  calculatedCosts,
  onGoToSettings,
}) => {
  return (
    <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20" data-testid="cost-estimator-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Support Cost Estimator
        </h2>
        <button
          onClick={onGoToSettings}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-secondary bg-bg-dark border border-text-muted/20 rounded-lg hover:border-accent/40 hover:text-accent transition"
          data-testid="adjust-prices-link"
        >
          <Settings className="w-4 h-4" />
          Adjust Prices
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="bg-bg-dark p-4 rounded-lg">
          <p className="text-text-secondary text-sm mb-1">Support Volume</p>
          <p className="text-white text-xl font-bold">
            {(costEstimate.support_volume_mm3 / 1000).toFixed(1)} cm³
          </p>
        </div>
        <div className="bg-bg-dark p-4 rounded-lg">
          <p className="text-text-secondary text-sm mb-1">Filament Length</p>
          <p className="text-white text-xl font-bold">
            {costEstimate.filament_length_m?.toFixed(1) || '0'} m
          </p>
        </div>
        <div className="bg-bg-dark p-4 rounded-lg">
          <p className="text-text-secondary text-sm mb-1">Print Time (Supports)</p>
          <p className="text-white text-xl font-bold">
            {costEstimate.print_time_estimate || 'N/A'}
          </p>
        </div>
        <div className="bg-bg-dark p-4 rounded-lg">
          <p className="text-text-secondary text-sm mb-1">vs Model Size</p>
          <p className="text-white text-xl font-bold">
            {costEstimate.support_vs_model_percent?.toFixed(1) || '0'}%
          </p>
        </div>
      </div>

      {/* Material Costs Table — read-only */}
      <div className="mb-4">
        <h3 className="text-white font-semibold mb-3">Filament Usage & Cost by Material</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-text-muted/20">
                <th className="text-left text-text-secondary py-2 px-3">Material</th>
                <th className="text-right text-text-secondary py-2 px-3">Weight</th>
                <th className="text-right text-text-secondary py-2 px-3">Price/kg</th>
                <th className="text-right text-text-secondary py-2 px-3">Est. Cost</th>
              </tr>
            </thead>
            <tbody>
              {costEstimate.filament_usage_grams && Object.entries(costEstimate.filament_usage_grams).map(([material, grams]) => (
                <tr key={material} className="border-b border-text-muted/10 hover:bg-bg-dark/50">
                  <td className="py-2 px-3 text-white font-medium">{material}</td>
                  <td className="py-2 px-3 text-right text-text-secondary">{grams.toFixed(2)}g</td>
                  <td className="py-2 px-3 text-right text-text-secondary">
                    ${customPrices[material] || DEFAULT_PRICES[material] || 20}
                  </td>
                  <td className="py-2 px-3 text-right text-green-400 font-medium">
                    ${calculatedCosts[material] || '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assumptions */}
      <div className="text-xs text-text-secondary bg-bg-dark/50 p-3 rounded">
        <p className="font-medium mb-1">Assumptions:</p>
        <p>Layer height: {costEstimate.assumptions?.layer_height_mm || 0.2}mm | 
           Support infill: {costEstimate.assumptions?.support_infill_percent || 15}% | 
           Print speed: {costEstimate.assumptions?.print_speed_mm_s || 40}mm/s</p>
      </div>
    </div>
  );
};

export default SupportResults;
